import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { apiFetch } from '../services/apiClient';

// Distinct teardrop pins: the JOB pin (navy) never moves; the DELIVERY pin
// (orange) is the draggable, more-specific drop point for large jobs.
const pin = (color, size) => L.divIcon({
  className: '',
  html: `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.45)"></div>`,
  iconSize: [size, size], iconAnchor: [size / 2, size],
});
const JOB_PIN = pin('#1e3a5f', 18);
const DELIVERY_PIN = pin('#e87722', 24);

function ClickToPlace({ onPlace }) {
  useMapEvents({ click(e) { onPlace(e.latlng.lat, e.latlng.lng); } });
  return null;
}

export default function DeliveryLocationModal({ projectId, initial, onSave, onClose }) {
  const [jobLoc, setJobLoc] = useState(undefined); // undefined=loading, null=none
  const [pt, setPt] = useState(initial && initial.lat != null ? { lat: initial.lat, lng: initial.lng } : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch('/projects-map')
      .then(rows => {
        const p = (Array.isArray(rows) ? rows : []).find(x => String(x.id) === String(projectId));
        const jl = p && p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, address: p.address } : null;
        setJobLoc(jl);
        setPt(prev => prev || (jl ? { lat: jl.lat, lng: jl.lng } : null));
      })
      .catch(() => setJobLoc(null));
  }, [projectId]);

  const center = pt || jobLoc || { lat: 39.5296, lng: -119.8138 }; // Reno fallback

  async function save() {
    if (!pt) { onClose(); return; }
    setSaving(true);
    let address = null;
    try { const r = await apiFetch(`/geocode/reverse?lat=${pt.lat}&lng=${pt.lng}`); address = r && r.display_name; } catch {}
    onSave({ lat: pt.lat, lng: pt.lng, address });
    setSaving(false);
    onClose();
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 680, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#1e3a5f', padding: '12px 16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>🗺 Set the delivery drop point</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '8px 16px', fontSize: 12.5, color: '#374151', borderBottom: '1px solid #eef0f2', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#1e3a5f', borderRadius: '50%', marginRight: 5 }} />Job location</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#e87722', borderRadius: '50%', marginRight: 5 }} />Delivery drop — tap the map or drag the orange pin</span>
        </div>
        {jobLoc === undefined ? (
          <div style={{ padding: 50, textAlign: 'center', color: '#9ca3af' }}>Loading map…</div>
        ) : (
          <>
            <MapContainer center={[center.lat, center.lng]} zoom={16} style={{ height: 420, width: '100%' }}>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <ClickToPlace onPlace={(lat, lng) => setPt({ lat, lng })} />
              {jobLoc && <Marker position={[jobLoc.lat, jobLoc.lng]} icon={JOB_PIN}><Popup>Job location</Popup></Marker>}
              {pt && (
                <Marker position={[pt.lat, pt.lng]} icon={DELIVERY_PIN} draggable
                  eventHandlers={{ dragend: e => { const ll = e.target.getLatLng(); setPt({ lat: ll.lat, lng: ll.lng }); } }}>
                  <Popup>Delivery drop point</Popup>
                </Marker>
              )}
            </MapContainer>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #eef0f2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{pt ? `Drop point: ${pt.lat.toFixed(5)}, ${pt.lng.toFixed(5)}` : 'Tap the map to drop a point'}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} disabled={!pt || saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: !pt || saving ? '#ccc' : '#e87722', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save drop point'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
